import React from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { Box, Typography, useTheme, Button, Chip } from '@mui/material'
import { Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import MiniDepthChart from '../components/charts/MiniDepthChart'
import { PRESET_RANGES } from '../config'
import { useSensorLocations } from '../layers/sensor_locations'
import { useEventDepthData } from '../layers/event_depth_data_binsearch_dictcomp'
import { usePastFloodEvents } from '../layers/past_flood_events'
import type { GeoJsonFeature } from '../utils/ogc'
import { SensorLegendTable } from '../components/scrubber/SensorDataGrid'
import NavBar from '../components/menu/NavBar'

export const Route = createFileRoute('/events')({
  component: PresetsPage,
})

type AugmentedSensor = GeoJsonFeature & {
  properties: GeoJsonFeature['properties'] & {
    time: Date[]
    depth_inches: number[]
    past_events: GeoJsonFeature[]
  }
}

const parsedPresets = PRESET_RANGES.map((p) => ({
  label: p.label,
  from: new Date(p.fromTime),
  to: new Date(p.toTime),
}))
const BOROUGH_PALETTE = ['#0e9089', '#1f6feb', '#ef6c00', '#8e24aa', '#2d6a4f']

export default function PresetsPage() {
  const theme = useTheme()
  const { features: sensors, loading: sensorsLoading } = useSensorLocations()
  const [globalHighlight, setGlobalHighlight] = React.useState<string | null>(null)

  return (
    <>
    <NavBar />
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        p: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              px: 1.5,
              py: 0.75,
              borderRadius: 999,
              fontWeight: 800,
              fontSize: 12,
              letterSpacing: 0.3,
              textTransform: 'uppercase',
              color: theme.palette.mode === 'dark' ? '#e8f7ff' : '#0b3a3a',
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, rgba(14,144,137,0.3), rgba(0,200,255,0.35))'
                : 'linear-gradient(135deg, #e6fffb, #d8f2ff)',
              boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
            }}
          >
            FloodNet NYC
          </Box>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 2 }}>
            Flood Monitoring
          </Typography>
        </Box>
        <Typography variant="h3" sx={{ fontWeight: 900, mb: 1, letterSpacing: '-0.5px' }}>
          Major Storm Events
        </Typography>
        <Typography variant="h6" color="text.secondary" sx={{ mb: 3, maxWidth: 840 }}>
          A short list of major NYC flooding captured by FloodNet. 
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gap: 3,
            gridTemplateColumns: '1fr',
          }}
        >
          {parsedPresets.map((preset, i) => (
            <PresetCard
              key={preset.label}
              preset={preset}
              sensors={sensors}
              loadingSensors={sensorsLoading}
              delay={80 * i}
              globalHighlight={globalHighlight}
              onGlobalHighlightChange={setGlobalHighlight}
            />
          ))}
        </Box>
      </Box>
    </Box>
    </>
  )
}

function PresetCard({
  preset,
  sensors,
  loadingSensors,
  delay,
  globalHighlight,
  onGlobalHighlightChange,
}: {
  preset: { label: string; from: Date; to: Date }
  sensors: GeoJsonFeature[] | undefined
  loadingSensors: boolean
  delay?: number
  globalHighlight: string | null
  onGlobalHighlightChange: (k: string | null) => void
}) {
  const theme = useTheme()
  const range = React.useMemo<[Date, Date, boolean]>(() => [preset.from, preset.to, false], [preset.from, preset.to])
  const { features: depth, loading: depthLoading } = useEventDepthData(range)
  const { features: pastEvents, loading: pastLoading } = usePastFloodEvents(range)

  const features = React.useMemo<AugmentedSensor[] | undefined>(() => {
    if (!sensors) return undefined
    const out: Record<string, AugmentedSensor> = {}
    sensors.forEach((f) => {
      const id = f.properties?.id ?? f.properties?.deployment_id ?? ''
      out[id] = {
        ...f,
        properties: {
          ...f.properties,
          time: [],
          depth_inches: [],
          past_events: [],
          labels: [],
          max_depth_inches: 0,
          current_depth_inches: undefined,
        },
      }
    })
    depth?.forEach((d) => {
      const fid = d.properties.deployment_id
      const target = out[fid]
      if (!target) return
      target.properties.time.push(d.properties.time)
      target.properties.depth_inches.push(d.properties.depth_inches)
      target.properties.labels.push(d.properties.label)
      target.properties.max_depth_inches = Math.max(target.properties.max_depth_inches || 0, d.properties.depth_inches)
    })
    pastEvents?.forEach((e) => {
      const fid = e.properties.deployment_id
      const target = out[fid]
      if (!target) return
      target.properties.past_events.push(e)
    })
    return Object.values(out).filter((f) => f.properties.time.length || f.properties.past_events.length)
  }, [sensors, depth, pastEvents])

  const loading = loadingSensors || depthLoading || pastLoading
  const [visible, setVisible] = React.useState(false)
  const [selectedBorough, setSelectedBorough] = React.useState<string | null>(null)
  React.useEffect(() => {
    const id = window.setTimeout(() => setVisible(true), delay ?? 0)
    return () => window.clearTimeout(id)
  }, [delay])
  const boroughCounts = React.useMemo(() => {
    const counts: Record<string, number> = {}
    features?.forEach((f) => {
      const b = (f.properties as any)?.borough
      if (!b) return
      counts[b] = (counts[b] || 0) + 1
    })
    return Object.entries(counts).sort((a, b) => b[1] - a[1])
  }, [features])
  const filteredFeatures = React.useMemo(() => {
    if (!selectedBorough) return features || []
    return (features || []).filter((f) => (f.properties as any)?.borough === selectedBorough)
  }, [features, selectedBorough])
  const handleFeatureClick = React.useCallback(
    (f: GeoJsonFeature) => {
      const id = (f.properties as any)?.deployment_id ?? (f.properties as any)?.id ?? f.id ?? ''
      onGlobalHighlightChange((prev) => (prev === id ? null : id))
    },
    [onGlobalHighlightChange]
  )

  return (
    <Box
      sx={{
        p: 3,
        borderRadius: 3,
        border: '1px solid',
        borderColor: theme.palette.divider,
        background: 'background.paper',
        backdropFilter: 'blur(14px)',
        boxShadow: theme.palette.mode === 'dark'
          ? '0 10px 40px rgba(0,0,0,0.35)'
          : '0 15px 40px rgba(0,0,0,0.12)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0px)' : 'translateY(8px)',
        transition: 'opacity 400ms ease, transform 400ms ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
      <Box>
      <Typography variant="h6" sx={{ fontWeight: 800 }}>
        {preset.label} 
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {format(preset.from, 'PPp')} — {format(preset.to, 'PPp')}
      </Typography>
      </Box>
        <Button
          variant="text"
          size="small"
          component={Link}
          to="/"
          search={{ from: preset.from.toISOString(), to: preset.to.toISOString() }}
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Open in Map
        </Button>
      </Box>
      {boroughCounts.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1 }}>
          {boroughCounts.map(([borough, count], idx) => {
            const color = BOROUGH_PALETTE[idx % BOROUGH_PALETTE.length]
            const active = selectedBorough === borough
            return (
              <Chip
                key={borough}
                label={`${borough}: ${count}`}
                size="small"
                clickable
                onClick={() => setSelectedBorough((prev) => (prev === borough ? null : borough))}
                sx={{
                  bgcolor: active
                    ? color
                    : color + (theme.palette.mode === 'dark' ? '33' : '22'),
                  color: active
                    ? '#ffffff'
                    : theme.palette.mode === 'dark' ? '#e8f7ff' : color,
                  fontWeight: 700,
                  letterSpacing: 0.2,
                  border: '1px solid',
                  borderColor: color + (theme.palette.mode === 'dark' ? '55' : '55'),
                  boxShadow: active ? '0 6px 16px rgba(0,0,0,0.25)' : undefined,
                }}
              />
            )
          })}
        </Box>
      )}
      <MiniDepthChart
        timeRange={range}
        currentTime={undefined}
        features={filteredFeatures}
        precip={[]}
        highlightedKey={globalHighlight}
        height={200}
        loading={loading}
        onClick={handleFeatureClick}
        caption={
          filteredFeatures.length
            ? `${filteredFeatures.length} sensor${filteredFeatures.length === 1 ? '' : 's'} with flooding${selectedBorough ? ` in ${selectedBorough}` : ''}`
            : undefined
        }
      />
      <Box sx={{ mt: 1 }}>
        <SensorLegendTable
          features={filteredFeatures as any}
          currentTime={undefined}
          highlightedKey={globalHighlight}
          onSelect={(f) => {
            const id = (f.properties as any)?.id
            onGlobalHighlightChange((prev) => (prev === id ? null : id))
          }}
        />
      </Box>
      {/* <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
        
      </Box> */}
    </Box>
  )
}
